import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { Icon } from 'assets/icons';
import { useModuleService } from 'hooks/api/sales/module';
import React, { useEffect } from 'react';
import styles from './invoice.module.css';

export const InvoiceItemRow = ({
  item,
  currency = 'NGN',
  existing_subscriptions = [],
  has_subscription = false,
  onChange = (f) => f,
  onRemove = (f) => f
}) => {
  const { fetchModules } = useModuleService();

  const CREDIT_RATES = { NGN: 1, KES: 0.08, GHS: 0.0098, USD: 0.00083 };
  const PERIODS = [
    { label: 'Monthly', value: 'month' },
    { label: 'Yearly', value: 'year' }
  ];

  const [contact_brackets, setContactBrackets] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [contact_bracket, setContactBracket] = React.useState({});
  const [selected_product, setSelectedProduct] = React.useState({});
  const [period, setPeriod] = React.useState(PERIODS[0]);
  const [cost, setCost] = React.useState(0);
  const [discount_rate, setDiscountRate] = React.useState(0);
  const [units, setUnits] = React.useState(0);

  useEffect(() => {
    fetchModules().then(({ modules }) => {
      const parsed_modules = modules.map((mod) => ({ ...mod, label: mod.title, value: mod.code }));
      setProducts([
        ...parsed_modules,
        { label: 'Email credits', value: 'email_credits' },
        { label: 'SMS credits', value: 'sms_credits' },
        { label: 'Push credits', value: 'push_credits' }
      ]);
    });
  }, []);

  useEffect(() => {
    if (
      !selected_product.value ||
      ['email_credits', 'sms_credits', 'push_credits'].includes(selected_product.value)
    ) {
      return;
    }

    setCost(0);

    const { pricing } = selected_product;
    const brackets = pricing.map((pricing) => {
      const { contact_bracket, discounts, prices } = pricing;
      const price = prices.find((p) => p.currency === currency);
      return {
        label: `${contact_bracket.min} - ${contact_bracket.max}`,
        value: `${contact_bracket.min}:${contact_bracket.max}`,
        cost: price ? price.amount : 0,
        discounts
      };
    });

    setContactBrackets(brackets);
    setContactBracket(brackets[0] || {});
  }, [selected_product.value, currency]);

  useEffect(() => {
    if (
      !contact_bracket.value ||
      ['email_credits', 'sms_credits', 'push_credits'].includes(selected_product.value)
    )
      return;
    const { value, cycles, starts_at, expires_at } = contact_bracket?.discounts[period.value] || {};
    const used_subs = existing_subscriptions.filter(
      (sub) =>
        sub.time_stamp >= starts_at &&
        sub.plan_code === selected_product.value &&
        sub.renewal_period === period.value
    );
    const has_used_up_discounts = cycles > 0 && used_subs.length >= cycles;
    const can_use_discount = !has_used_up_discounts && expires_at && expires_at > Date.now();

    const discount_percent = can_use_discount ? value : 0;
    const item_cost = period.value === 'year' ? contact_bracket.cost * 12 : contact_bracket.cost;
    setCost(item_cost);
    setDiscountRate(discount_percent);
  }, [contact_bracket.value, period.value, currency, existing_subscriptions]);

  useEffect(() => {
    const rate = CREDIT_RATES[currency] || 1;
    setCost(units * rate);
    setDiscountRate(0);
  }, [units, currency]);

  useEffect(() => {
    onChange(item.key, {
      ...item,
      discount_rate,
      product: selected_product,
      contact_bracket,
      period,
      cost,
      units
    });
  }, [cost]);

  const is_credit_product = ['email_credits', 'sms_credits', 'push_credits'].includes(
    selected_product.value
  );
  return (
    <div className={styles.itemRow}>
      <span>
        <GridRow num_of_columns={4}>
          <GridColumn>
            <DetailItem title="Product">
              <MultiSelect
                options={products}
                value={selected_product}
                onChange={setSelectedProduct}
              />
            </DetailItem>
          </GridColumn>
          {!is_credit_product && (
            <GridColumn>
              <DetailItem title="Contact bracket">
                <MultiSelect
                  options={contact_brackets}
                  value={contact_bracket}
                  onChange={setContactBracket}
                />
              </DetailItem>
            </GridColumn>
          )}
          {!is_credit_product && (
            <GridColumn>
              <DetailItem title="Period">
                <MultiSelect options={PERIODS} value={period} onChange={setPeriod} />
              </DetailItem>
            </GridColumn>
          )}
          {is_credit_product && (
            <GridColumn span={2}>
              <DetailItem title="Units">
                <SimpleInput value={units} onInput={setUnits} />
              </DetailItem>
            </GridColumn>
          )}
          <GridColumn>
            <DetailItem title="Cost">
              <SimpleInput disabled value={cost} />
            </DetailItem>
          </GridColumn>
        </GridRow>
      </span>
      <span className={styles.removeIcon}>
        <Icon name="trash" onClick={() => onRemove(item.key)} />
      </span>
    </div>
  );
};
